import _, { get } from "lodash";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";

import { Button, cn } from "@shared/ui";
import { Eye, EyeOff } from "lucide-react";
import { Field, InlineError, inputClassName } from "../components/Field";
import CarrierMark from "../components/CarrierMark";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { useReport } from "../components/report/ReportProvider";
import {
  CARRIERS_WITH_ZIPCODE_LOGIN,
  MFA_VALID_ENROLLMENT_MESSAGE,
} from "../constants";
import {
  isAwaiting,
  isStarted,
  login,
  Operation,
  processDirectLink,
  updateIgnition,
} from "../lib/axle";
import { MAX_HITL_STARTED_RETRIES } from "../lib/constants";
import { createEvent } from "../lib/ignitionEvents";
import {
  checkZipCode,
  getIntegrationConfig,
  isIntegrationV123,
  useAccountHandler,
} from "../lib/utility";
import CarrierLoader from "./CarrierLoader";

// Friendly banner copy for a durable login-wait (the run suspended on a
// recoverable credential failure and is waiting for re-entered credentials).
const loginAwaitingMessage = (reason) => {
  switch (reason) {
    case "login-page-not-reached":
      return "We couldn't reach your carrier's login page. Please double-check your details and try again.";
    case "credential-error":
    default:
      return "We couldn't sign in with those credentials. Please double-check your username and password and try again.";
  }
};

// Structural/dispatch failures from a submitCredentials re-entry call — never a
// credential-class outcome. Anything else on a reentry hard-failure means the
// backend's bounded login-wait loop exhausted its attempts and gave up for good.
const REENTRY_DISPATCH_ERROR_MESSAGES = new Set([
  "credentials submission must include either credentialId or username and password",
  "Session is not awaiting credentials",
  "Failed to submit credentials",
]);

// Carrier logins can take up to ~90 seconds; keep the status moving without claiming specific progress.
const getLoginLoadingSchedule = (carrierName) => ({
  steps: [
    { at: 0, text: "Encrypting your credentials" },
    { at: 2.5, text: "Establishing a secure connection" },
    { at: 5, text: `Contacting ${carrierName}` },
    { at: 7.5, text: "Signing in to your account" },
    { at: 10, text: "Verifying your login" },
    { at: 12.5, text: "Retrieving account information" },
    { at: 15, text: "Looking for your policies" },
    { at: 20, text: "Gathering policy details" },
    { at: 24, text: "Checking your coverage" },
    { at: 28, text: "Organizing your policy information" },
    { at: 32, text: "Reviewing policy documents" },
    { at: 36, text: "Still retrieving your information" },
  ],
  waiting: {
    from: 45,
    every: 6,
    messages: [
      `Waiting for ${carrierName} to respond`,
      "Still working on it",
      "Thanks for your patience",
      "Hang tight, we’re on it",
    ],
  },
  longWait: {
    at: 90,
    text: "This is taking longer than usual. Thanks for waiting",
  },
});

const FIELD_ERRORS = {
  zipcode: "Enter a valid policy zipcode",
  username: "Enter your username",
  password: "Enter your password",
};

const Login = ({
  step,
  nextStep,
  setAccountInfo,
  loginInformation,
  setLoginInformation,
  session,
  previousStep,
  setLinkError,
  posthog,
  loginAttempts,
  setLoginAttempts,
  serverErrorAttempts,
  setServerErrorAttempts,
  setShowNav,
  cognitoAuthTokens,
  integration,
  client,
}) => {
  const carrier = get(loginInformation, "carrier.id");
  const ignitionToken = session.id;
  const reporting = useReport();
  // With no result, this clears the previous attempt's fields.
  const captureLoginAttempt = (result) =>
    reporting?.captureAttempt("login", {
      integration,
      carrierId: carrier,
      loginPath: isIntegrationV123(loginInformation, integration, session)
        ? "v123"
        : "legacy",
      // Legacy failures carry errorCode; a V123 login-wait stores its code as awaitingReason.
      loginErrorCode: get(result, "errorCode") ?? get(result, "awaitingReason"),
    });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [zipcode, setZipCode] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState("");
  const [promiseInProgress, setPromiseInProgress] = useState(false);
  const [relink, setRelink] = useState(false);
  // null = normal first login; a number = the run is suspended at a login-wait
  // and the next submit re-enters credentials into that SAME run. The value is
  // the wait round from the awaiting response; it is UI state only — the submit
  // carries its own freshly generated idempotency key.
  const [awaitingAttempt, setAwaitingAttempt] = useState(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // When a credential field is focused: mark the form as "input focused" (the
  // outer form toggles overflow-y so mobile scroll + keyboard behavior stays sane),
  // then scroll the field into view on the next frame so it is not hidden under
  // the soft keyboard. We keep a reference to the DOM node because by the time
  // requestAnimationFrame runs, React's event may no longer expose currentTarget.
  const handleInputFocus = (e) => {
    setIsInputFocused(true);

    const el = e.currentTarget;
    requestAnimationFrame(() => {
      if (el == null || !document.documentElement.contains(el)) return;
      el.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "auto",
      });
    });
  };

  // Restore outer form overflow when no credential field is focused.
  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  // Retrieve the registration and recovery urls from the integration config in the carrier config

  // useMemo() to memoize integration config, the integration, integration display name so it doesn't re-run on every keystroke
  const integrationConfig = useMemo(
    () => getIntegrationConfig(session, loginInformation, integration),
    [session, loginInformation, integration]
  );

  const registerUrl = get(integrationConfig, "registerUrl", false);
  const recoveryUrl = get(integrationConfig, "recoveryUrl", false);

  const isManualEnabled = get(session, "config.manual.enabled", false);

  // Memoized so _.find doesn't re-run on every keystroke — session, loginInformation,
  // and integration are stable while the user is typing.
  const matchingIntegration = useMemo(
    () =>
      _.find(get(loginInformation, "carrier.integrations", []), {
        integration,
      }),
    [loginInformation, integration]
  );

  const integrationDisplayName = useMemo(
    () =>
      get(matchingIntegration, "displayName") ??
      get(loginInformation, "carrier.name", ""),
    [matchingIntegration, loginInformation]
  );

  const passwordless = useMemo(
    () => get(matchingIntegration, "passwordless", false),
    [matchingIntegration]
  );

  const isLoginSupportEnabled = useMemo(
    () => registerUrl || recoveryUrl || isManualEnabled,
    [registerUrl, recoveryUrl, isManualEnabled]
  );

  const hasZipcodeLogin = useMemo(
    () =>
      CARRIERS_WITH_ZIPCODE_LOGIN.includes(get(loginInformation, "carrier.id")),
    [loginInformation]
  );

  let retryLoginAttempts = 0;
  let retryGetAccountAttempts = 0;

  const getAccount = useAccountHandler(
    {
      ignitionToken,
      integration,
      setAccountInfo,
      loginInformation,
      nextStep,
      setLinkError,
      setShowNav,
      retryGetAccountAttempts,
    },
    setLoginInformation
  );

  const retryLoginGetAccount = async () => {
    if (retryLoginAttempts < 1) {
      retryLoginAttempts++;
      console.log("Received an error from login, retrying...");
      await loginGetAccount(relink);
    } else {
      setLinkError("login");
      nextStep("carrier-error", relink);
    }
  };

  const handleUnsuccessfulLogin = async (error) => {
    captureLoginAttempt(error);
    switch (get(error, "code")) {
      case 400: {
        switch (error.message) {
          case "Username not found. Please try again":
          case "Username or password incorrect. Please try again":
            // Increment loginAttempt counter and display remaining attempts if incorrect username/password
            // Send to max login attempts if 3 or more unsuccessful attempts
            if (get(loginAttempts, carrier, 0) + 1 > 2) {
              nextStep("max-login-attempts", relink);
              return;
            }

            // Increment login attempt counter
            setLoginAttempts({
              ...loginAttempts,
              [carrier]: get(loginAttempts, carrier, 0) + 1,
            });

            // Update error message
            error.message = `Username or password incorrect. You have ${
              3 - (get(loginAttempts, carrier, 0) + 1)
            } ${
              3 - (get(loginAttempts, carrier, 0) + 1) === 1
                ? "attempt"
                : "attempts"
            } remaining.`;
            setPromiseInProgress(false);
            setError(error.message);
            return;
          case "Sorry, your account is locked. Please reset your password and then try again!":
            setPromiseInProgress(false);
            setError(error.message);
            return;
          case "Sandbox only supports test credentials. Please try again.":
            setPromiseInProgress(false);
            setError(error.message);
            return;
          case "Only username and password login is supported for this carrier. Update your carrier account settings so password login is available, then try again.":
            setPromiseInProgress(false);
            setError(error.message);
            return;
          case "Sorry, you are attempting to login with credentials to the wrong carrier. Please confirm the brand shown on your policy documents and visit the correct carrier login page.":
            setPromiseInProgress(false);
            setError(error.message);
            return;
          case "Sorry, your insurance account or policy has not been set up for online access.":
            nextStep("account-pending", relink);
            return;
          case "MFA enrollment is required for your insurance account. Please enroll in MFA and try again.":
            nextStep("mfa-enrollment-required", relink);
            return;
          case MFA_VALID_ENROLLMENT_MESSAGE:
            setLinkError(error.message);
            nextStep("mfa-options-error", relink);
            return;
          case "Sorry, your AAA chapter is not supported by Axle.":
            nextStep("aaa-chapter-error", relink);
            return;
          default:
            console.log("Unknown 400 error from login: ", error);
            setLinkError("login");
            nextStep("carrier-error", relink);
            return;
        }
      }
      case 503: {
        // This code is always used for carrier maintenance
        nextStep("carrier-maintenance", relink);
        return;
      }
      // Found that retrying 401 session expired responses can be helpful
      case 401: {
        switch (error.message) {
          case "Session expired":
            console.log("401 error from login: ", error);
            break;
          default:
            console.error("Unknown 401 error from login: ", error);
        }
        await retryLoginGetAccount();
        return;
      }
      case 403: // Found that retrying 403 forbidden responses can be helpful
        switch (error.message) {
          case "Forbidden":
            console.log("403 error from login: ", error);
            break;
          default:
            console.error("Unknown 403 error from login: ", error);
        }
        await retryLoginGetAccount();
        return;
      case 500: {
        switch (error.message) {
          case "Oops! Something went wrong.":
          case "Oops something went wrong. Please try again later.":
            console.log("500 error from login: ", error);
            break;
          default:
            console.error("Unknown 500 error from login: ", error);
        }

        const currentServerErrorAttempts = get(serverErrorAttempts, carrier, 0);

        // if this is the first time we're seeing a server error, automatically retry
        if (currentServerErrorAttempts === 0) {
          await retryLoginGetAccount();
        }

        setServerErrorAttempts({
          ...serverErrorAttempts,
          [carrier]: currentServerErrorAttempts + 1,
        });

        if (currentServerErrorAttempts > 2) {
          nextStep("max-server-login-attempts", relink);
        } else {
          setLinkError("login");
          nextStep("carrier-error", relink);
        }
        return;
      }
      default:
        console.error("Unknown error from login: ", error);
        // If an error occurs, and retry attempts are less than 1, retry logging in
        await retryLoginGetAccount();
        return;
    }
  };

  const loginGetAccount = async (isRelink, startedPollAttempts = 0) => {
    if (startedPollAttempts === 0) captureLoginAttempt();
    // Remove the nav bar while loading
    setShowNav(false);

    if (isIntegrationV123(loginInformation, integration, session)) {
      // On a login-wait re-entry, target the SAME suspended run via
      // submitCredentials with a fresh idempotency key; otherwise start the run
      // with the natural login op. Only the first call of a re-entry dispatches
      // credentials — the started re-polls that follow must not (see below) —
      // but the whole re-entry keeps its error routing.
      const isReentry = awaitingAttempt !== null;
      const isReentryDispatch = isReentry && startedPollAttempts === 0;
      const operation = isReentryDispatch
        ? Operation.SUBMIT_CREDENTIALS
        : Operation.LOGIN;
      const data = isReentryDispatch
        ? // A NEW idempotency key for every submit. The wait round we were
          // re-prompted on (`awaitingAttempt`) cannot advance while login is
          // still in flight, so reusing it got the resubmit deduplicated and
          // silently dropped. Generated here, at the one call per submit
          // action, so the backend's dispatch retries still share it.
          { username, password, zipcode, submissionId: nanoid() }
        : {
            ignitionToken,
            username,
            password,
            zipcode,
            account: get(loginInformation, "account"),
            token: cognitoAuthTokens?.idToken?.toString(),
            integration,
          };
      const [directLinkResponse, directLinkErrorResponse] =
        await processDirectLink(ignitionToken, integration, operation, data);
      if (directLinkErrorResponse) {
        if (isReentry) {
          captureLoginAttempt(directLinkErrorResponse);
          setAwaitingAttempt(null);
          setPromiseInProgress(false);
          setShowNav(true);
          // A structural/dispatch failure (bad request, stale session, backend
          // hiccup) is not a lockout — send it to a generic error screen instead
          // of telling the user they're out of attempts. Anything else hitting
          // this branch is a credential-class failure the loop declined to
          // re-arm, i.e. attempts are genuinely exhausted.
          if (
            REENTRY_DISPATCH_ERROR_MESSAGES.has(
              get(directLinkErrorResponse, "message")
            )
          ) {
            setLinkError("login");
            nextStep("carrier-error", isRelink);
          } else {
            nextStep("max-login-attempts", isRelink);
          }
          return;
        }
        await handleUnsuccessfulLogin(directLinkErrorResponse);
        setShowNav(true);
        return;
      }

      // The run suspended at a login-wait (recoverable bad credentials). Keep the
      // form, surface the reason, and arm the next submit to re-enter into the
      // same run with an attempt-distinct nonce. Attempts are bounded by the
      // backend; when exhausted the run errors instead (handled above).
      if (isAwaiting(directLinkResponse)) {
        captureLoginAttempt(directLinkResponse);
        setAwaitingAttempt(
          get(directLinkResponse, "attempt", awaitingAttempt ?? 0)
        );
        setError(loginAwaitingMessage(get(directLinkResponse, "awaitingReason")));
        setPromiseInProgress(false);
        setShowNav(true);
        return;
      }

      if (get(directLinkResponse, "status") === "completed") {
        setAwaitingAttempt(null);
        // Do NOT short-circuit a resumed session to getAccount here: this step's
        // getAccount is wired with setLoginInformation, which stamps
        // result: "link" — the flag isIntegrationV123 reads as "legacy run".
        // Confirm would then post the policy to linkPolicy and never complete the
        // browser agent's policy-wait callback. SendMfa owns that skip instead.
        nextStep("sendMfa", isRelink);
      } else if (
        isStarted(directLinkResponse) &&
        startedPollAttempts < MAX_HITL_STARTED_RETRIES
      ) {
        // Still logging in. Re-poll the same run with the plain login op:
        // hitlAi skips the vault-and-dispatch when a VncSessions row already
        // exists, so this only polls. submitCredentials must NOT repeat — its
        // wait is consumed by the first dispatch, and a second one 409s
        // "Session is not awaiting credentials".
        return loginGetAccount(isRelink, startedPollAttempts + 1);
      } else {
        // No account to fetch, so getAccount would only report "no policies".
        setLinkError("login");
        nextStep("carrier-error", isRelink);
        setShowNav(true);
      }

      return;
    }
    const [loginResponse, loginErrorResponse] = await login(
      ignitionToken,
      carrier,
      username,
      password,
      zipcode,
      get(loginInformation, "account"),
      cognitoAuthTokens?.idToken?.toString(),
      integration
    );

    if (loginErrorResponse) {
      // Handle failed login
      console.log("Login did not return a 200 status code...");
      await handleUnsuccessfulLogin(loginErrorResponse);

      // Restore the nav bar
      setShowNav(true);
      return;
    }

    // Handle successful login
    console.log("Login was successful, parsing response...");

    // If MFA required, send to MFA
    if (get(loginResponse, "mfaRequired")) {
      // Set login result to link
      setLoginInformation({
        ...loginInformation,
        result: "link",
        resultDetail: "user-portal",
        integration,
      });

      nextStep("sendMfa", isRelink);

      // Restore the nav bar
      setShowNav(true);
      return;
    }

    // We don't know if this case exists, throwing an alarm in case it ever happens (12/18/24)
    // We believe it means that the carrier we logged into doesn't exist?
    else if (get(loginResponse, "isNotFound")) {
      console.error("Login returned isNotFound, this should not happen.");
      setError("We had an error contacting your carrier. Please try again.");
      setPromiseInProgress(false);
      setShowNav(true);
      return;
    }

    // If carrier is part of basic flow, send to manual input or unsupported carrier
    else if (get(loginResponse, "isBasic")) {
      // Set login result to basic
      setLoginInformation({
        ...loginInformation,
        result: "basic",
        resultDetail: "basic",
      });

      nextStep(
        _(session).get("config.basic.manual") && isManualEnabled
          ? "manual-account"
          : "basic-policy-info",
        isRelink
      );

      // Restore the nav bar
      setShowNav(true);
      return;
    }

    // If none of the above cases apply, we can process to the link, getAccount process
    await getAccount(isRelink);

    // Set login result to basic
    setLoginInformation({
      ...loginInformation,
      result: "link",
      resultDetail: "user-portal",
    });
  };

  const onSubmit = async (e) => {
    // Prevent default form behavior
    e.preventDefault();

    if (
      CARRIERS_WITH_ZIPCODE_LOGIN.includes(
        get(loginInformation, "carrier.id")
      ) &&
      !checkZipCode(zipcode)
    ) {
      setError(FIELD_ERRORS.zipcode);
      return;
    }

    // Validate input requirements
    if (!username) {
      setError(FIELD_ERRORS.username);
      return;
    }

    if (!passwordless && !password) {
      setError(FIELD_ERRORS.password);
      return;
    }

    // Track login submit
    // Only track if a username and password are entered
    posthog.capture("login-submit", {
      step,
      carrier,
      loginAttempt: get(loginAttempts, carrier, 0) + 1,
    });

    // Remove the nav bar while loading
    setShowNav(false);

    // Mark this session as having attempted a link, regardless of successful
    // or not — used to compute link-attempt-based conversion metrics
    await createEvent({
      ignitionToken,
      status: "attempted",
      result: "link",
      resultDetail: "user-portal",
      session,
      client,
      carrier,
      integration,
    });

    setPromiseInProgress(true);
    await loginGetAccount(relink);
  };

  // Setup login state
  useEffect(() => {
    posthog.capture("$pageview", { step, carrier });

    // If welcome back feature flag is on, check if we're in a returning link flow,
    // and if the user has idToken and account information
    if (
      session.returningUser === "re-link" &&
      get(cognitoAuthTokens, "idToken") &&
      get(loginInformation, "account")
    ) {
      console.log("Setting relink link to true");
      // We're in a returning link flow
      setRelink(true);
      console.log(
        "Found cognito token and account information, auto-logging in..."
      );
      // Start login process
      setPromiseInProgress(true);
      // Pass true directly here to ensure the proper value is used
      loginGetAccount(true);
      return;
    }

    // Update ignition session with carrier
    const updateIgnitionLogin = async () => {
      const [updateIgnitionResponse, updateIgnitionError] =
        await updateIgnition(session.id, {
          carrier,
          integration,
        });

      if (updateIgnitionError) {
        switch (get(updateIgnitionError, "code")) {
          case 500:
            console.log(JSON.stringify(updateIgnitionError));
            nextStep("axle-error", relink);
            return;
          default:
            nextStep("connection-error", relink);
            return;
        }
      }
    };

    updateIgnitionLogin();

    // Get updated session
    const getLoginAttempts = async () => {
      // Reset login attempts if the user is coming from max attempts page
      if (previousStep === "max-login-attempts") {
        setLoginAttempts((prevAttempts) => ({
          ...prevAttempts,
          [carrier]: 0,
        }));
      } else {
        // Send to max login attempts if 3 or more unsuccessful attempts
        if (get(loginAttempts, carrier) > 2) {
          nextStep("max-login-attempts", relink);
          return;
        }

        // Set remaining attempts error message if any unsuccessful attempts
        if (get(loginAttempts, carrier) > 0) {
          setError(
            `Username or password incorrect. You have ${
              3 - get(loginAttempts, carrier, 0)
            } ${
              3 - get(loginAttempts, carrier, 0) === 1 ? "attempt" : "attempts"
            } remaining.`
          );
        }

        // Update login attempts
        setLoginAttempts({
          ...loginAttempts,
          [carrier]: get(loginAttempts, carrier, 0),
        });
      }
    };

    getLoginAttempts();
  }, [posthog]);

  // Missing-field errors show under their field; anything else (e.g. carrier credential errors) shows in the box above.
  const fieldError = _.findKey(FIELD_ERRORS, (message) => message === error);
  const isFieldInvalid = (field) => Boolean(error) && (!fieldError || fieldError === field);
  const errorDescribedBy = (id, field) => {
    if (!error) return undefined;
    if (!fieldError) return "login-error";
    return fieldError === field ? `${id}-error` : undefined;
  };

  return (
    <>
      {promiseInProgress ? (
        <CarrierLoader
          loginInformation={loginInformation}
          loadingHeader={`Connecting to ${integrationDisplayName}`}
          loadingSchedule={getLoginLoadingSchedule(integrationDisplayName)}
        />
      ) : (
        <>
          <form
            onSubmit={onSubmit}
            className={`flex min-h-0 flex-1 flex-col ${
              hasZipcodeLogin ? "zipcode-present" : ""
            } ${isInputFocused ? "overflow-y-hidden" : "overflow-y-auto"}`}
          >
            <StepBody>
              {/* login-header-icons is the hook index.css uses to hide the logo on short screens */}
              <CarrierMark className="login-header-icons" loginInformation={loginInformation} />
              {/* gap-y-2 wrapper is the hook index.css uses to shrink this heading on short screens */}
              <div className="flex flex-col gap-y-2">
                <StepHeading
                  title={`Log in to ${integrationDisplayName}`}
                  description={`Enter the same login you use for ${integrationDisplayName}.`}
                />
              </div>

              <div
                className="flex flex-col gap-y-8 grow flex-1 min-h-0"
                onSubmit={onSubmit}
              >
                <div className="flex flex-col gap-y-4 login-input-fields pb-40">
                  {error && !fieldError && (
                    <div className="rounded border border-danger-border p-3">
                      <InlineError id="login-error">{error}</InlineError>
                    </div>
                  )}

                  <Field
                    id="login-username"
                    label="Username"
                    error={fieldError === "username" ? error : undefined}
                  >
                    <input
                      id="login-username"
                      value={username}
                      className={inputClassName}
                      type="text"
                      autoCapitalize="off"
                      autoCorrect="off"
                      aria-invalid={isFieldInvalid("username")}
                      aria-describedby={errorDescribedBy("login-username", "username")}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </Field>

                  {!passwordless && (
                    <Field
                      id="login-password"
                      label="Password"
                      error={fieldError === "password" ? error : undefined}
                    >
                      <div className="relative">
                        <input
                          id="login-password"
                          value={password}
                          type={passwordVisible ? "text" : "password"}
                          autoCapitalize="off"
                          autoCorrect="off"
                          aria-invalid={isFieldInvalid("password")}
                          aria-describedby={errorDescribedBy("login-password", "password")}
                          className={cn(inputClassName, "pr-12")}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          aria-label={
                            passwordVisible ? "Hide password" : "Show password"
                          }
                          aria-pressed={passwordVisible}
                          className="absolute right-0.5 top-1/2 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded text-ink-secondary hover:text-ink"
                        >
                          {passwordVisible ? (
                            <EyeOff
                              aria-hidden
                              className="size-[18px]"
                              strokeWidth={1.75}
                            />
                          ) : (
                            <Eye
                              aria-hidden
                              className="size-[18px]"
                              strokeWidth={1.75}
                            />
                          )}
                        </button>
                      </div>
                    </Field>
                  )}

                  {CARRIERS_WITH_ZIPCODE_LOGIN.includes(
                    get(loginInformation, "carrier.id")
                  ) ? (
                    <Field
                      id="login-zipcode"
                      label="Policy zipcode"
                      error={fieldError === "zipcode" ? error : undefined}
                    >
                      <input
                        id="login-zipcode"
                        placeholder="e.g. 94107"
                        value={zipcode}
                        className={inputClassName}
                        type="text"
                        aria-invalid={isFieldInvalid("zipcode")}
                        aria-describedby={errorDescribedBy("login-zipcode", "zipcode")}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onChange={(e) => setZipCode(e.target.value)}
                      />
                    </Field>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </StepBody>

            <StepFooter>
              <Button type={"submit"}>Connect</Button>
              {isLoginSupportEnabled && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => nextStep("login-support", relink)}
                >
                  I don't know my login information
                </Button>
              )}
            </StepFooter>
          </form>
        </>
      )}
    </>
  );
};

export default Login;
